import type { TimelineTypeFilter } from '@/enums/TimelineTypeFilter'

const userProfileKeys = {
  all: ['userProfiles'] as const,
  notifications: (userProfileId: Ref<number>) => [...userProfileKeys.all, userProfileId, 'notifications'] as const,
  notificationsPage: (userProfileId: Ref<number>, page: Ref<number>) => [...userProfileKeys.notifications(userProfileId), page] as const,
  unreadNotifications: (userProfileId: Ref<number>) => [...userProfileKeys.notifications(userProfileId), 'unread'] as const,
}

const activityKeys = {
  all: ['activities'] as const,
  overviews: () => [...activityKeys.all, 'overviews'] as const,
  overview: (organisationId: Ref<number>, filters: Ref<object>) => [...activityKeys.overviews(), organisationId, filters] as const,
  activity: (activityId: Ref<number>) => [...activityKeys.all, 'activity', activityId] as const,
  activityStudents: (activityId: Ref<number>) => [...activityKeys.activity(activityId), 'students'] as const,
  activityMessages: (activityId: Ref<number>) => [...activityKeys.activity(activityId), 'messages'] as const,
  studentActivityMessages: (studentActivityId: Ref<number>, activityId: Ref<number>) => [...activityKeys.activityMessages(activityId), 'student-activity-messages', studentActivityId] as const,
  creates: () => [...activityKeys.all, 'create'] as const,
  create: (organisationId: Ref<number>) => [...activityKeys.creates(), organisationId] as const,
}

const agreementKeys = {
  all: ['agreements'] as const,
  details: () => [...agreementKeys.all, 'detail'] as const,
  detail: (id: Ref<number>) => [...agreementKeys.details(), id] as const,
  licenceTypes: (id: Ref<number>) => [...agreementKeys.detail(id), 'licenceTypes'] as const,
}

const organisationKeys = {
  all: ['organisations'] as const,
  details: () => [...organisationKeys.all, 'detail'] as const,
  detail: (id: Ref<number>) => [...organisationKeys.details(), id] as const,
  libraryFolders: (id: Ref<number>) => [...organisationKeys.detail(id), 'libraryFolders'] as const,
  libraryFolder: (id: Ref<number>, libraryFolderId: Ref<number>) => [...organisationKeys.libraryFolders(id), libraryFolderId] as const,
  libraryFiles: (id: Ref<number>, libraryFolderId: Ref<number>) => [...organisationKeys.libraryFolder(id, libraryFolderId), 'libraryFiles'] as const,
  libraryFileSearch: (id: Ref<number>, search: Ref<string>) => [...organisationKeys.detail(id), 'search', search] as const,
  academicYears: (id: Ref<number>) => [...organisationKeys.detail(id), 'academicYears'] as const,
  defaultProgrammeSettings: (id: Ref<number>) => [...organisationKeys.detail(id), 'defaultProgrammeSettings'] as const,
  licenceTypes: (id: Ref<number>) => [...organisationKeys.detail(id), 'licenceTypes'] as const,
  resources: (id: Ref<number>) => [...organisationKeys.detail(id), 'resources'] as const,
  settings: (id: Ref<number>) => [...organisationKeys.detail(id), 'settings'] as const,
  programmes: (id: Ref<number>, isInterviewsHandled?: Ref<boolean>) => [...organisationKeys.detail(id), 'programmes', isInterviewsHandled] as const,
}

const groupKeys = {
  all: ['groups'] as const,
  lists: () => [...groupKeys.all, 'list'] as const,
  list: (academicYearId: Ref<number>) => [...groupKeys.lists(), academicYearId] as const,
}

const helpKeys = {
  all: ['help'] as const,
  details: () => [...helpKeys.all, 'detail'] as const,
  detail: (id: Ref<string>) => [...helpKeys.details(), id] as const,
  docs: (id: Ref<string>) => [...helpKeys.detail(id), 'docs'] as const,
}

// TODO: Use this for all interview session related queries
const interviewSessionsKeys = {
  all: ['interviewSessions'] as const,
  session: (id: Ref<number>) => [...interviewSessionsKeys.all, id] as const,
  interviewerDays: (id: Ref<number>) => [...interviewSessionsKeys.session(id), 'interviewerDays'] as const,
  unallocatedStudents: (id: Ref<number>) => [...interviewSessionsKeys.session(id), 'unallocatedStudents'] as const,
  allocatedStudents: (id: Ref<number>) => [...interviewSessionsKeys.session(id), 'allocatedStudents'] as const,
}

const interviewerDaysKeys = {
  all: ['interviewerDays'] as const,
  session: (id: Ref<number>) => [...interviewerDaysKeys.all, id] as const,
  date: (interviewSessionId: Ref<number>, date: Ref<Date | null>) => [...interviewerDaysKeys.session(interviewSessionId), date] as const,
}

const studentsKeys = {
  all: ['students'] as const,
  unallocated: (interviewSessionId: Ref<number>) => [...studentsKeys.all, 'unallocated', interviewSessionId] as const,
  activities: (studentId: Ref<number>) => [...studentsKeys.all, 'activities', studentId] as const,
  allocated: (interviewSessionId: Ref<number>) => [...studentsKeys.all, 'allocated', interviewSessionId] as const,
  overviews: () => [...studentsKeys.all, 'overviews'] as const,
  overview: (organisationId: Ref<number>, filters: Ref<object>) => [...studentsKeys.overviews(), organisationId, filters] as const,
  allStudentIds: (organisationId: Ref<number>, filters: Ref<object>) => [...studentsKeys.overview(organisationId, filters), 'allStudentIds'] as const,
  details: () => [...studentsKeys.all, 'detail'] as const,
  detail: (id: Ref<number>) => [...studentsKeys.details(), id] as const,
  search: (organisationId: Ref<number>, search: Ref<string>) => ['search', organisationId, search] as const,
  timeline: (id: Ref<number>) => [...studentsKeys.detail(id), 'timeline'] as const,
  timelineList: (id: Ref<number>, programmeFilter: Ref<TimelineTypeFilter>, academicYearFilter: Ref<object>) => [...studentsKeys.timeline(id), 'list', programmeFilter, academicYearFilter] as const,
  timelineComments: (id: Ref<number>) => [...studentsKeys.timeline(id), 'comments'] as const,
  upcomingInterviews: (id: Ref<number>) => [...studentsKeys.detail(id), 'upcomingInterviews'] as const,
  pastInterviews: (id: Ref<number>) => [...studentsKeys.detail(id), 'pastInterviews'] as const,
  creates: () => [...studentsKeys.all, 'create'] as const,
  create: (organisationId: Ref<number>) => [...studentsKeys.creates(), organisationId] as const,
}

const academicYearsKeys = {
  all: ['academicYears'] as const,
  allStats: () => [...academicYearsKeys.all, 'stats'] as const,
  stats: (academicYearId: Ref<number>) => [...academicYearsKeys.allStats(), academicYearId] as const,
  programmeStats: (academicYearId: Ref<number>, programmeId: Ref<number>) => [...academicYearsKeys.stats(academicYearId), programmeId] as const,
  details: () => [...academicYearsKeys.all, 'detail'] as const,
  detail: (id: Ref<number>) => [...academicYearsKeys.details(), id] as const,
  selfRegistrations: (id: Ref<number>) => [...academicYearsKeys.detail(id), 'selfRegistrations'] as const,
}

const programmesKeys = {
  all: ['programmes'] as const,
  stats: () => [...programmesKeys.all, 'stats'] as const,
  details: () => [...programmesKeys.all, 'detail'] as const,
  detail: (id: Ref<number>) => [...programmesKeys.details(), id] as const,
  reports: (id: Ref<number>) => [...programmesKeys.detail(id), 'reports'] as const,
}

const studentProgrammesKeys = {
  all: ['studentProgrammes'] as const,
  overviews: () => [...studentProgrammesKeys.all, 'overviews'] as const,
  overview: (organisationId: Ref<number>) => [...studentProgrammesKeys.overviews(), organisationId] as const,
  overviewMy1C: (organisationId: Ref<number>, student_programme_ids: Ref<number[]>) => [...studentProgrammesKeys.overview(organisationId), 'my1C', student_programme_ids] as const,
  overviewMyUC: (organisationId: Ref<number>, student_programme_ids: Ref<number[]>) => [...studentProgrammesKeys.overview(organisationId), 'myUC', student_programme_ids] as const,
  overviewMyCC: (organisationId: Ref<number>, programmeId: Ref<number>, student_programme_ids: Ref<number[]>) => [...studentProgrammesKeys.overview(organisationId), 'myCC', programmeId, student_programme_ids] as const,
  overviewMyA: (organisationId: Ref<number>, programmeId: Ref<number>, student_programme_ids: Ref<number[]>) => [...studentProgrammesKeys.overview(organisationId), 'myA', programmeId, student_programme_ids] as const,
  details: () => [...studentProgrammesKeys.all, 'detail'] as const,
  detail: (id: Ref<number>) => [...studentProgrammesKeys.details(), id] as const,
  studentNotes: (id: Ref<number>) => [...studentProgrammesKeys.detail(id), 'studentNotes'] as const,
  myUC: (id: Ref<number>, type: Ref<string>) => [...studentProgrammesKeys.detail(id), 'myUC', type] as const,
  my1C: (id: Ref<number>) => [...studentProgrammesKeys.detail(id), 'my1C'] as const,
  myCC: (id: Ref<number>, areaResultLimit: Ref<number>) => [...studentProgrammesKeys.detail(id), 'myCC', areaResultLimit] as const,
  myA: (id: Ref<number>) => [...studentProgrammesKeys.detail(id), 'myA'] as const,
  myAResults: (id: Ref<number>) => [...studentProgrammesKeys.myA(id), 'results'] as const,
  myAProgress: (id: Ref<number>) => [...studentProgrammesKeys.myA(id), 'progress'] as const,
}

export {
  userProfileKeys,
  activityKeys,
  academicYearsKeys,
  agreementKeys,
  groupKeys,
  helpKeys,
  interviewerDaysKeys,
  organisationKeys,
  programmesKeys,
  studentProgrammesKeys,
  studentsKeys,
}
